import { useEffect, useId, useRef, useState, type ReactNode } from "react";

export function requestModalClose(element: HTMLElement, close: () => void) {
  const dialog = element.closest<HTMLElement>("[role=dialog]");
  const warning = dialog?.dataset.closeWarning || (dialog?.dataset.dirty === "true" ? "Изменения не сохранены. Закрыть окно и потерять их?" : "");
  if (!warning || window.confirm(warning)) close();
}

export default function Modal({ title, onClose, children, wide = false, closeWarning = "" }: {
  title: string; onClose: () => void; children: ReactNode; wide?: boolean; closeWarning?: string;
}) {
  const ref = useRef<HTMLElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const id = useId();
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    const dialog = ref.current!;
    const previous = document.activeElement as HTMLElement | null;
    const focusable = () => Array.from(dialog.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex="0"]')).filter((element) => element.getClientRects().length > 0);
    if (!dialog.contains(document.activeElement)) (dialog.querySelector<HTMLElement>("input, select, textarea") ?? focusable()[0] ?? dialog).focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); requestModalClose(dialog, closeRef.current); }
      if (event.key === "Tab") {
        const elements = focusable();
        const first = elements[0] ?? dialog, last = elements[elements.length - 1] ?? dialog;
        if (!dialog.contains(document.activeElement) || (event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
          event.preventDefault(); (event.shiftKey ? last : first).focus();
        }
      }
    };
    const unload = (event: BeforeUnloadEvent) => { if (dialog.dataset.dirty === "true" || dialog.dataset.closeWarning) { event.preventDefault(); event.returnValue = ""; } };
    document.addEventListener("keydown", keydown, true);
    window.addEventListener("beforeunload", unload);
    return () => { document.removeEventListener("keydown", keydown, true); window.removeEventListener("beforeunload", unload); if (previous?.isConnected) previous.focus(); };
  }, []);
  const close = () => { if (ref.current) requestModalClose(ref.current, onClose); };
  return <div className="modal-bg" onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}>
    <section ref={ref} role="dialog" aria-modal="true" aria-labelledby={id} tabIndex={-1} data-dirty={dirty} data-close-warning={closeWarning} className={`modal${wide ? " wide" : ""}`} onChangeCapture={() => setDirty(true)}>
      <header><h2 id={id}>{title}</h2><button type="button" aria-label="Закрыть окно" onClick={close}>×</button></header>{children}
    </section>
  </div>;
}
